<script>
	import { has_access, get_user_slug } from '#lib/util.js';
  import { page } from '$app/state';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import NotificationBadge from '#lib/NotificationBadge.svelte';
  import Divider from '#lib/Divider.svelte';
  import Avatar from '#lib/Avatar.svelte';
  import t from '#lib/i18n.svelte.js';
  import { getContext } from 'svelte';
  import AccountSwitcher from '#lib/AccountSwitcher.svelte';
  import { register_click_outside_element } from '#lib/click_outside.svelte.js';

  let site_settings = getContext('site_settings');
  let menu_container = $state();
  let is_open = $state(false);

  $effect(() => {
    if (is_open && menu_container) {
      const unregister = register_click_outside_element({
        el: menu_container,
        on_click_outside: () => {
          is_open = false;
        }
      });
      return unregister;
    }
  });

  // CUSTOM_CODE_STARTS (PATCH_WORK)
  let needs_to_add_payment_method = $derived.by(() => {
    // Return true if property exists and is false, and user is not admin
    return 'has_stripe_payment_method' in page.data.current_user && 
      !page.data.current_user.has_stripe_payment_method &&
      page.data.authenticated_user?.user_id === page.data.current_user?.user_id &&
      !page.data.account_switcher_users?.length && // has no delegators (which could have a payment method)
      page.data.current_user.role !== 'admin';
  });
  let needs_to_add_billing_address = $derived.by(() => {
    // Return true if property exists and is false, and user is not admin
    const result = 'has_stripe_billing_address' in page.data.current_user && 
      !page.data.current_user.has_stripe_billing_address &&
      page.data.authenticated_user?.user_id === page.data.current_user?.user_id &&
      !page.data.account_switcher_users?.length && // has no delegators (which could have a billing address)
      page.data.current_user.role !== 'admin';    
    return result;
  });
  // CUSTOM_CODE_ENDS (PATCH_WORK)

  // Show tasks if there's an open task
  let open_tasks_count = $derived.by(() => {
    return [
      // CUSTOM_CODE_STARTS (PATCH_WORK)
      needs_to_add_payment_method ? 1 : 0,
      needs_to_add_billing_address ? 1 : 0,
      // CUSTOM_CODE_ENDS (PATCH_WORK)
    ].reduce((a, b) => a + b, 0);
  });
  let notification_count = $derived.by(()=>{
    return [
      page.data.unread_conversations,
      open_tasks_count
    ].reduce((a, b) => a + b, 0);
  });
</script>

<div class="account-menu-container relative flex-row items-center" bind:this={menu_container}>
  <button 
    class="account-menu-button"
    onclick={() => is_open = !is_open}
    aria-expanded={is_open}
    aria-haspopup="true"
    aria-label={t('account_menu')}
    style='--button-outer-block-spacing: var(--default-button-outer-block-spacing)'
  >
    <ButtonLabel 
      align='center' 
      variant='brand ghostly text-compact'
    >
      {#snippet start_icon()}
        <Avatar
          avatar={page.data.current_user.avatar}
          name={page.data.current_user.name}
          animate={true}
        />
      {/snippet}
    </ButtonLabel>
    {#if notification_count > 0 && !is_open}
      <NotificationBadge>{notification_count}</NotificationBadge>
    {/if}
  </button>


  {#if is_open}
    <div class='absolute-popover flex-column' role='dialog'>
      <!-- Messages -->
      <a
        href='/messages'
        class='messages-link flex-row'
        title={t('messages')}
        data-sveltekit-preload-data="false"
      >
        <ButtonLabel 
          variant='brand ghostly' 
          start_icon='paperplane' 
          align='start-stretch'
          text={t('messages')}
        >
        {#snippet end_icon()}
          {#if page.data.unread_conversations > 0}
            <NotificationBadge placement='inline-end'>{page.data.unread_conversations}</NotificationBadge>
          {/if}  
        {/snippet}
      </ButtonLabel>
      </a>

      <!-- My Event Registrations -->
      <a href='/account/my-event-registrations'>
        <ButtonLabel 
          variant='brand ghostly' 
          start_icon='calendar'
          align='start-stretch'
          text_style='text-control'
        >
          {t('my_event_registrations')}
        </ButtonLabel>
      </a>

      <!-- Invoices: the Stripe customer portal, once the member has a Stripe
           customer. No preload: every visit opens a portal session. -->
      {#if page.data.current_user?.stripe_customer_id}
        <a href='/create-customer-portal-session' data-sveltekit-preload-data='false' data-sveltekit-reload>
          <ButtonLabel
            variant='brand ghostly'
            start_icon='invoice'
            align='start-stretch'
            text_style='text-control'
          >
            {t('invoices')}
          </ButtonLabel>
        </a>
      {/if}

      <!-- My Collections -->
      {#if has_access('member', page.data.current_user?.role)}
        <a 
          href={`/${get_user_slug(page.data.current_user.user_id, page.data.current_user.name)}/collections`} 
        >
          <ButtonLabel 
            variant='brand ghostly' 
            start_icon='bookmarks'
            align='start-stretch'
          >
            {t('my_collections')}
          </ButtonLabel>
        </a>
      {/if}

      <!-- Tasks Section -->
      {#if open_tasks_count > 0}
        <Divider class='px p-4'>
          <div class='flex-row text-control text-3 items-center'>
            {t('open_tasks')}
            <NotificationBadge placement='inline-end'>{open_tasks_count}</NotificationBadge>
          </div>  
        </Divider>
        <div class="account-tasks flex-column">
          <!-- List of open tasks ... -->
          <!-- CUSTOM_CODE_STARTS (PATCH_WORK) -->
          {#if needs_to_add_payment_method}
            <a data-sveltekit-preload-data="false" data-sveltekit-reload href="/create-customer-portal-session">
              <ButtonLabel variant='brand ghostly' start_icon='box' align='start-stretch' text_style='text-control'>
                {t('add_payment_details')}
              </ButtonLabel>
            </a>
          {/if}
          {#if needs_to_add_billing_address}
            <a data-sveltekit-preload-data="false" data-sveltekit-reload href="/create-customer-portal-session">
              <ButtonLabel variant='brand ghostly' start_icon='box' align='start-stretch' text_style='text-control'>
                {t('add_billing_address')}
              </ButtonLabel>
            </a>
          {/if}
          <!-- CUSTOM_CODE_ENDS (PATCH_WORK) -->
        </div>
      {/if}
      
      <Divider class='px p-4' label={t('settings')} />

      <!-- Account settings -->
      <a 
        href='/account/settings'
        title={t('account')}
      >
        <ButtonLabel 
          variant='brand ghostly' 
          start_icon='calm-face' 
          align='start-stretch'
        >{t('account_settings')}</ButtonLabel>
      </a>
  
      {#if page.data.current_user.role === 'admin'}
        <a 
          href={`/settings`} 
          data-sveltekit-preload-data="false" 
          data-sveltekit-reload
        >
          <ButtonLabel 
            align='start-stretch'
            start_icon='settings'
            variant='brand ghostly'
          >
            {t('website')}
          </ButtonLabel>
        </a>
        <!-- CUSTOM_CODE_STARTS (PATCH_WORK) -->
        <Divider class='px p-4'  label={t('billing')} />
        <a href="/pricing-and-capacity">
          <ButtonLabel variant='brand ghostly' start_icon='invoice' align='start-stretch'>
            {t('pricing_and_capacity')}
          </ButtonLabel>
        </a>
        <a href='/admin/billing-log'>
          <ButtonLabel variant='brand ghostly' start_icon='list' align='start-stretch'>
            {t('billing_log')}
          </ButtonLabel>
        </a>
        <a href='https://dashboard.stripe.com/' target='_blank'>
          <ButtonLabel variant='brand ghostly' start_icon='external-link' align='start-stretch'>
            Stripe
          </ButtonLabel>
        </a>
        <!-- CUSTOM_CODE_ENDS (PATCH_WORK) -->
      {/if}

      {#if page.data.can_use_email_signature}
        <a href='/signature'>
          <ButtonLabel
            align='start-stretch'
            start_icon='email'
            text_style='text-control'
            variant='brand ghostly'
          >
            {t('my_email_signature')}
          </ButtonLabel>
        </a>
      {/if}

      <Divider />
      <a 
        href={`/logout?return_to=${encodeURIComponent(page.url.pathname.substring(1))}`} 
        data-sveltekit-preload-data="false"
        data-sveltekit-reload
      >
        <ButtonLabel 
          align='start-stretch' 
          start_icon='exit'
          variant='brand ghostly'
        >
          {t('logout')}
        </ButtonLabel>
      </a>

      {#if page.data.account_switcher_users?.length > 1 || page.data.current_user.role === 'admin'}
        <Divider class='px p-4' label={t('switch_account')} />
        <div class='px py p-2'>
          <AccountSwitcher 
            container='inline'
            on_switch_user={() => {
              is_open = false;
            }} 
          />
        </div>
      {/if}

    </div>
  {/if}
</div>

<style lang="postcss">
  :global(.account-menu-button .avatar) {
    border-radius: 100%;
  }
  .account-menu-container :global(.account-switcher .select-list) {
    max-height: none;
  }
  /* Match menu item contrast: muted tokens (--text-3, --color-default-stroke,
     --color-default-foreground-300) stay too dim on dark menu surfaces. */
  .absolute-popover {
    color: var(--color-foreground, var(--color-default-foreground));
    --icon-color1: var(--color-foreground, var(--color-default-foreground));
    --divider-color: color-mix(
      in oklch,
      var(--color-foreground, var(--color-default-foreground)) 28%,
      transparent
    );
  }
  .absolute-popover :global(.divider > span) {
    color: color-mix(
      in oklch,
      var(--color-foreground, var(--color-default-foreground)) 70%,
      transparent
    );
  }
</style>